import { DefaultUser, Membership, Subscription } from '@ligo/shared/auth';
import { computed, ComputedRef } from '@vue/composition-api';
import moment from 'moment';
import { useStore, useRouter, useI18n } from '../../composables';
import { PlanItem } from '../../models';

const localeMapper = { 'en-us': 'en', nl: 'nl' };
const MEMBERSHIP_TYPE_TIME = {
  monthly: 1,
  yearly: 12,
  semi_yearly: 6,
  quarterly: 3
};

export function useMembershipInfo() {
  const store = useStore();
  const router = useRouter();
  const i18n = useI18n();
  const lang = localeMapper[i18n.locale];

  const user: ComputedRef<DefaultUser> = computed(
    () => store.getters['authentication/user']
  );

  const membership: ComputedRef<Membership> = computed(
    () => store.getters['authentication/getMembership']
  );

  const subscription: ComputedRef<Subscription> = computed(
    () => store.getters['authentication/getSubscription']
  );

  const currentType = computed(() => {
    if (
      membership.value?.membership_type.price >
      subscription.value?.subscription_type.price
    ) {
      return membership.value?.membership_type;
    } else {
      return subscription.value?.subscription_type;
    }
  });

  const hasMembership = computed(() => currentType.value?.price > 0);

  const userHasFreeMonths = computed(() =>
    user.value?.free_months > 0 ? user.value?.free_months : false
  );

  const membershipTypeSlug = computed(() => {
    return (
      membership.value?.membership_type.slug ||
      subscription.value?.subscription_type.name
    );
  });

  const availableSignatures = computed(
    () =>
      membership.value?.available_signatures ??
      subscription.value?.signature_usage?.max -
        subscription.value?.signature_usage?.consumed
  );
  const availableTemplates = computed(
    () =>
      membership.value?.available_templates ??
      subscription.value?.template_usage?.max -
        subscription.value?.template_usage?.consumed
  );
  const didReceiveContractForFree = computed(
    () => !!user.value?.did_receive_contract_for_free
  );
  const planSignatures = computed(
    () =>
      membership.value?.membership_type.signatures ??
      subscription.value?.signature_usage?.max
  );
  const planTemplates = computed(
    () =>
      membership.value?.membership_type.templates ??
      subscription.value?.template_usage?.max
  );

  const membershipName = computed(() => {
    if (currentType.value?.description) {
      return (
        JSON.parse(currentType.value?.description)[lang].name ||
        currentType.value?.name
      );
    } else return '';
  });

  const membershipBillingInterval = computed(
    () => currentType.value.billing_interval
  );

  const membershipPrice = computed(() => currentType.value.price);

  const membershipFeatures = computed(() => {
    const data = JSON.parse(membership.value.membership_type.description)[lang];
    return data.features as PlanItem[];
  });

  const nextBillingDate = computed(() => {
    const dateFormat = 'MMM Do YYYY';
    if (subscription.value?.next_payment_date) {
      return moment(subscription.value?.next_payment_date).format(dateFormat);
    } else {
      const nextMomentBillingDate = moment(membership.value?.next_billing_date);
      const billingIntervalValue = mapMembershipBillingInterval(
        membershipBillingInterval.value
      );
      const hasFreeMonths = userHasFreeMonths.value;
      const resultDate = nextMomentBillingDate.clone();
      if (hasFreeMonths) {
        resultDate.add(hasFreeMonths * billingIntervalValue, 'months');
      }

      return resultDate.format(dateFormat);
    }
  });

  const downgradeDate = computed(() => {
    // TODO: we need to define a new action for reactivate the membership
    if (!user.value?.downgrade_date) return false;
    return moment(user.value?.downgrade_date).format('MMM Do YYYY');
  });

  function redirectToUpgrade() {
    router.push({ name: 'LegalPlans' });
  }

  function mapMembershipBillingInterval(interval: string) {
    return MEMBERSHIP_TYPE_TIME[interval] || 0;
  }

  return {
    membership,
    hasMembership,
    subscription,
    currentType,
    downgradeDate,
    redirectToUpgrade,
    availableSignatures,
    availableTemplates,
    planSignatures,
    planTemplates,
    membershipName,
    membershipFeatures,
    nextBillingDate,
    membershipBillingInterval,
    membershipPrice,
    didReceiveContractForFree,
    membershipTypeSlug
  };
}
